import {
  ArchiveFinanceBudgetDocument,
  ArchiveFinanceBudgetMutationVariables,
  BudgetOptionsDocument,
  CreateFinanceBudgetDocument,
  CreateFinanceBudgetMutationVariables,
  NewBudgetPageDocument,
  UnarchiveFinanceBudgetDocument,
  UnarchiveFinanceBudgetMutationVariables,
  UpdateFinanceBudgetDocument,
  UpdateFinanceBudgetMutationVariables,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'

export const useCreateBudget = () => {
  const [createBudget, { loading }] = useMutation(CreateFinanceBudgetDocument, {
    extractorFn: (data) => data.financeMutations.createFinanceBudget,
  })

  return {
    loading,
    createBudget: (variables: CreateFinanceBudgetMutationVariables) => createBudget({ variables }),
  }
}

export const useUpdateBudget = () => {
  const [updateBudget, { loading }] = useMutation(UpdateFinanceBudgetDocument, {
    extractorFn: (data) => data.financeMutations.updateFinanceBudget,
    refetchQueries: [],
  })

  return {
    loading,
    updateBudget: (variables: UpdateFinanceBudgetMutationVariables) => updateBudget({ variables }),
  }
}

export const useArchiveBudget = () => {
  const [archiveFinanceBudget, { loading }] = useMutation(ArchiveFinanceBudgetDocument, {
    extractorFn: (data) => data.financeMutations.archiveFinanceBudget,
    refetchQueries: [],
  })

  return {
    loading,
    archiveBudget: (variables: ArchiveFinanceBudgetMutationVariables) =>
      archiveFinanceBudget({ variables }),
  }
}

export const useUnarchiveBudget = () => {
  const [unarchiveFinanceBudget, { loading }] = useMutation(UnarchiveFinanceBudgetDocument, {
    extractorFn: (data) => data.financeMutations.unarchiveFinanceBudget,
    refetchQueries: [],
  })

  return {
    loading,
    unarchiveBudget: (variables: UnarchiveFinanceBudgetMutationVariables) =>
      unarchiveFinanceBudget({ variables }),
  }
}

export const useBudgetsPage = (companyId: string) => {
  const { data, loading } = useQuery(NewBudgetPageDocument, {
    variables: {
      companyId,
    },
  })
  const legalEntities = data?.apiCore?.company?.legalEntities ?? []
  const features = data?.features?.featuresConnection.nodes
  return {
    legalEntities,
    features,
    loading,
  }
}

export type BudgetOption = { value: string; label: string }
export type UseBudgetOptions = (params?: {
  effectiveOn?: string
  employeeId?: string
  skip?: boolean
}) => {
  loading: boolean
  budgetOptions: BudgetOption[]
}

export const useBudgetOptions: UseBudgetOptions = ({ effectiveOn, employeeId, skip } = {}) => {
  const todayDateString = new Date().toISOString().split('T')[0]

  const { data, loading } = useQuery(BudgetOptionsDocument, {
    variables: {
      employeeId: String(employeeId),
      effectiveAt: effectiveOn || todayDateString,
    },
    skip: !employeeId || skip,
  })
  const budgetOptions = (data?.finance.budgetOptionsConnection.nodes || []).map((budget) => ({
    value: budget.id,
    label: budget.name,
  }))

  return {
    loading,
    budgetOptions,
  }
}
